import React, { useState, useEffect } from 'react';
import { 
  Box, 
  useTheme, 
  Typography, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  useMediaQuery
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { tokens } from '../../theme';
import { useParams } from "react-router-dom";
import Header from "../../components/Header";
import CreateClassTimetable from './CreateClassTimetable';
import ChangeClassTimetable from './ChangeClassTimetable';
import useSessionTimeRangeService from '../../api/services/SessionTimeRangeService';
import axiosInstance from '../../api/axiosInstance';
import { endpoints } from '../../api/endPoints';
import { toast } from 'react-hot-toast';
import generateClassTimetablePDF from '../../utils/classTimetablePdf';
import { getUserFromCookies } from '../../utils/Cookie-utils';

const ClassTimetable = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const params = useParams();
  const classId = params.id;
  
  const user = getUserFromCookies();
  const userRole = user?.data?.role;
  const allowedRoles = ['ADMIN', 'MANAGER', 'ACADEMIC'];
  const canManageTimetable = allowedRoles.includes(userRole);
  
  const { getAllActiveSessionTimeRanges } = useSessionTimeRangeService();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isChangeDialogOpen, setIsChangeDialogOpen] = useState(false);
  const [timetableData, setTimetableData] = useState({});
  const [sessionTimes, setSessionTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classInfo, setClassInfo] = useState(null);

  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  useEffect(() => {
    if (classId) {
      loadTimetableData();
    } else {
      // If no classId, stop loading and show empty state
      setLoading(false);
      setSessionTimes([]);
      const empty = {};
      days.forEach(day => {
        empty[day] = {};
      });
      setTimetableData(empty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const loadTimetableData = async () => {
    setLoading(true);
    
    try {
      // Initialize empty structure first to ensure UI is ready
      const empty = {};
      days.forEach(day => {
        empty[day] = {}; // Each session time will contain an array
      });
      setTimetableData(empty);
      setSessionTimes([]);

      // Fetch session times
      try {
        const times = await getAllActiveSessionTimeRanges();
        if (Array.isArray(times)) {
          setSessionTimes(times);
        } else {
          setSessionTimes([]);
        }
      } catch (error) {
        setSessionTimes([]);
      }

      // Fetch class information
      try {
        const classResponse = await axiosInstance.get(`${endpoints.class.getClass}${classId}`);
        if (classResponse.data && classResponse.data.data) {
          setClassInfo(classResponse.data.data);
        }
      } catch (error) {
        // Silently fail - class info is optional
      }

      // Fetch timetable links for this class
      try {
        const response = await axiosInstance.get(`${endpoints.sessionTimeRanges.getAll}/class/${classId}/timetable`);
        const links = response.data.data || [];
        
        if (links.length > 0) {
          // Organize data by day and session time
          const organized = {};
          days.forEach(day => {
            organized[day] = {};
          });

          links.forEach(link => {
            if (link.day && link.sessionTime) {
              // Initialize as array if not exists
              if (!organized[link.day][link.sessionTime]) {
                organized[link.day][link.sessionTime] = [];
              }
              
              // Add entry to array (supports multiple combinations per session time)
              organized[link.day][link.sessionTime].push({
                subject: link.subjectName || null, // null for break times
                teacher: link.teacherName || null,
                combination: link.combination || null, // Combination for this link
                timetableId: link.timetableId || null, // Store for unlinking
                csrtId: link.csrtId || null, // Store for unlinking (null for break times)
                day: link.day, // Store for unlinking
              });
            }
          });

          setTimetableData(organized);
        }
      } catch (error) {
        // If endpoint fails, keep empty structure
        // Suppress 404 errors as this is expected if no links exist yet
      }
    } catch (error) {
      // Ensure we have empty structure even on unexpected errors
      const empty = {};
      days.forEach(day => {
        empty[day] = {};
      });
      setTimetableData(empty);
      setSessionTimes([]);
    } finally {
      // Always stop loading - this is critical and must be in finally block
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    loadTimetableData(); // Reload data after creating a link
  };

  const handleChangeDialogClose = () => {
    setIsChangeDialogOpen(false);
    loadTimetableData(); // Reload data after unlinking
  };

  const linkedSessionTimes = React.useMemo(() => {
    const linkedTimes = new Set();
    
    // Iterate through all days and collect session times that have links
    days.forEach(day => {
      if (timetableData[day]) {
        Object.keys(timetableData[day]).forEach(sessionTime => {
          const cellData = timetableData[day][sessionTime];
          // Include if it has data (array with entries)
          if (cellData && Array.isArray(cellData) && cellData.length > 0) {
            linkedTimes.add(sessionTime);
          }
        });
      }
    });
    
    // Convert Set to sorted array
    return Array.from(linkedTimes).sort();
  }, [timetableData]);

  if (loading) {
    return (
      <Box sx={{ m: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ m: 2 }}>
      <CreateClassTimetable
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={handleDialogClose}
        classId={classId}
      />

      <ChangeClassTimetable
        open={isChangeDialogOpen}
        onClose={() => setIsChangeDialogOpen(false)}
        onSuccess={handleChangeDialogClose}
        classId={classId}
      />

      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={2}
        flexDirection={isSmallScreen ? 'column' : 'row'}
        gap={isSmallScreen ? 2 : 0}
      >
        <Header title="CLASS TIMETABLE" />
        {canManageTimetable && (
          <Box 
            display="flex" 
            gap={1} 
            flexDirection={isSmallScreen ? 'column' : 'row'}
            width={isSmallScreen ? '100%' : 'auto'}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setIsDialogOpen(true)}
              sx={{ 
                minWidth: isSmallScreen ? '100%' : '150px',
                fontSize: isSmallScreen ? '0.875rem' : '1rem'
              }}
              fullWidth={isSmallScreen}
            >
              {isSmallScreen ? 'CREATE' : 'CREATE TIMETABLE'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setIsChangeDialogOpen(true)}
              sx={{ 
                minWidth: isSmallScreen ? '100%' : '150px',
                fontSize: isSmallScreen ? '0.875rem' : '1rem'
              }}
              disabled={linkedSessionTimes.length === 0}
              fullWidth={isSmallScreen}
            >
              {isSmallScreen ? 'CHANGE' : 'CHANGE TIMETABLE'}
            </Button>
            {linkedSessionTimes.length > 0 && (
              <IconButton
                color="primary"
                onClick={() => {
                  try {
                    generateClassTimetablePDF(
                      timetableData,
                      linkedSessionTimes,
                      days,
                      classInfo?.classTitle || classInfo?.className || 'Class Timetable'
                    );
                    toast.success('Timetable PDF generated successfully');
                  } catch (error) {
                    toast.error('Error generating PDF');
                  }
                }}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  ...(isSmallScreen && { width: '100%', height: '40px' })
                }}
                title="Download Timetable as PDF"
              >
                <DownloadIcon />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      {linkedSessionTimes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No timetable links available.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Click "CREATE TIMETABLE" to link session times with subjects and teachers.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: colors.primary[400] }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Day</TableCell>
                {linkedSessionTimes.map((time, i) => (
                  <TableCell key={i} sx={{ fontWeight: "bold" }}>
                    {time}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {days.map((day, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell sx={{ fontWeight: "bold" }}>{day}</TableCell>
                  {linkedSessionTimes.map((time, colIndex) => {
                    const cellDataArray = timetableData[day]?.[time];
                    return (
                      <TableCell key={colIndex}>
                        {cellDataArray && Array.isArray(cellDataArray) && cellDataArray.length > 0 ? (
                          <Box>
                            {cellDataArray.map((cellData, idx) => (
                              <Box 
                                key={idx} 
                                sx={{ 
                                  mb: idx < cellDataArray.length - 1 ? 1 : 0,
                                  pb: idx < cellDataArray.length - 1 ? 1 : 0,
                                  borderBottom: idx < cellDataArray.length - 1 ? '1px solid' : 'none',
                                  borderColor: 'divider'
                                }}
                              >
                                {cellData.subject ? (
                                  <>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {cellData.subject}
                                    </Typography>
                                    {cellData.combination && (
                                      <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                                        ({cellData.combination})
                                      </Typography>
                                    )}
                                    {cellData.teacher && (
                                      <Typography variant="caption" display="block" color="textSecondary">
                                        {cellData.teacher}
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                    Break Time
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ClassTimetable;
